// Main App Component with Routing for AAR Guest Management System
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import { syncViewAsSession } from './firebase/authService';

// Components
import Login from './components/Login';
import AuthAction from './components/AuthAction';
import Dashboard from './components/Dashboard';
import CheckIn from './components/CheckIn';
import CheckOut from './components/CheckOut';
import VolunteerCheckIn from './components/VolunteerCheckIn';
import Layout from './components/Layout';

// Pages
import AdminPanel from './pages/AdminPanel';
import Administration from './pages/Administration';
import Reports from './pages/Reports';
import TimelineReport from './pages/TimelineReport';
import DataCompleteness from './pages/DataCompleteness';
import GuestProfiles from './pages/GuestProfiles';
import VolunteerProfiles from './pages/VolunteerProfiles';
import DataSync from './pages/DataSync';
import GuestActivityEmailReports from './pages/GuestActivityEmailReports';
import PagerReport from './pages/PagerReport';
import GuestPickupReport from './pages/GuestPickupReport';
import IncidentReports from './pages/IncidentReports';
import VolunteerActivityLog from './pages/VolunteerActivityLog';
import ActivityLogs from './pages/ActivityLogs';
import OAuthCallback from './pages/OAuthCallback';

// Styles
import './App.css';
// Must stay last: its select rules resolve some cascade ties by source order.
import './styles/form-controls.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Settle any "View as" session before pages render, so a screen never
      // runs in view-as mode without the server-side write block in place.
      await syncViewAsSession(currentUser);
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
};

// Main App Component
function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/action" element={<AuthAction />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />

          {/* Protected Routes with Layout */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/check-in" element={<CheckIn />} />
            <Route path="/volunteer-check-in" element={<VolunteerCheckIn />} />
            <Route path="/check-out/:checkInId" element={<CheckOut />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/admin-panel" element={<AdminPanel />} />
            <Route path="/administration" element={<Administration />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/timeline-report" element={<TimelineReport />} />
            <Route path="/data-completeness" element={<DataCompleteness />} />
            <Route path="/guest-profiles" element={<GuestProfiles />} />
            <Route path="/volunteer-profiles" element={<VolunteerProfiles />} />
            <Route path="/admin/data-sync" element={<DataSync />} />
            <Route path="/admin/guest-activity-email-reports" element={<GuestActivityEmailReports />} />
            <Route path="/admin/pager-report" element={<PagerReport />} />
            <Route path="/admin/guest-pickup-report" element={<GuestPickupReport />} />
            <Route path="/admin/volunteer-activity-log" element={<VolunteerActivityLog />} />
            <Route path="/admin/activity-logs" element={<ActivityLogs />} />
            <Route path="/incident-reports" element={<IncidentReports />} />
          </Route>

          {/* Default Route - Check for OAuth code first */}
          <Route path="/" element={
            window.location.search.includes('code=')
              ? <OAuthCallback />
              : <Navigate to="/check-in" replace />
          } />

          {/* 404 Route */}
          <Route path="*" element={<Navigate to="/check-in" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
